import { IDataResult, ResponseStatus } from "../../common/models/Result";
import { DataUtils } from "../../common/views/DataUtils";
import { Utils } from "../../common/views/Utils";
import { UserGroupService } from "../services/UserGroupService";
import { ProductKeyModel } from "../models/ProductModel";
import {
    CodePickerModel, createCodePickerModel, createLinePickerModel, createSizePickerModel,
    LinePickerModel, ProductPickerModel, SizePickerModel
} from "../models/ProductPickerModels";
import { useState } from "react";

export function useProductPickerVM(key: ProductKeyModel, onChange: (key: ProductKeyModel) => void) {
    const groupService = new UserGroupService();

    const [pickers, setPickers] = useState<LinePickerModel[]>([]);
    const [selectedLine, setSelectedLine] = useState<LinePickerModel>(createLinePickerModel());
    const [selectedSize, setSelectedSize] = useState<SizePickerModel>(createSizePickerModel());
    const [selectedCode, setSelectedCode] = useState<CodePickerModel>(createCodePickerModel());

    const [lineListHash, setLineListHash] = useState(Utils.getRandomKey());
    const [sizeListHash, setSizeListHash] = useState(Utils.getRandomKey());
    const [codeListHash, setCodeListHash] = useState(Utils.getRandomKey());

    const getPicker = () => {
        setPickers([]);

        let request = groupService.getPicker(key.groupId);
        DataUtils.onRequest(request, onGetPickerResult);
    }

    const onGetPickerResult = (result: IDataResult) => {
        if (result.status === ResponseStatus.success) {
            let newPickers = result.data as LinePickerModel[];

            if (newPickers && newPickers.length > 0) {
                setPickers(newPickers);

                const defaultLine = newPickers.find(
                    (picker: LinePickerModel, index: number) => picker.id === key.lineId
                ) ?? newPickers[0];

                setSelection(defaultLine);
            }
        }

        onRefresh(true, true, true);
    }

    const setSelection = (defaultLine: LinePickerModel) => {
        let defaultSize = defaultLine.sizes[0];
        let defaultCode = defaultSize.codes[0];

        for (var i = 0; i < defaultLine.sizes.length; i++) {
            const size = defaultLine.sizes[i];

            let code = size.codes.find(
                (code: CodePickerModel, index: number) => code.product.productId === key.productId
            );

            if (code) {
                defaultSize = size;
                defaultCode = code;
                break;
            }
        }

        setSelectedLine(defaultLine);
        setSelectedSize(defaultSize);
        setSelectedCode(defaultCode);
    }

    const shouldShow = (items: ProductPickerModel[]) => {
        return items.length > 1 || (items.length === 1 && !Utils.isNullOrEmpty(items[0].name));
    }

    const onLineClick = (line: LinePickerModel) => {
        const lineChanged = selectedLine.id !== line.id;

        const size = line.sizes[0];
        const code = size.codes[0];

        setSelectedLine(line);
        setSelectedSize(size);
        setSelectedCode(code);

        onChange(code.product);
        onRefresh(false, lineChanged, lineChanged);
    }

    const onSizeClick = (size: SizePickerModel) => {
        const sizeChanged = selectedSize.name !== size.name;
        const code = size.codes[0];

        setSelectedSize(size);
        setSelectedCode(code);

        onChange(code.product);
        onRefresh(false, false, sizeChanged);
    }

    const onCodeClick = (code: CodePickerModel) => {
        setSelectedCode(code);

        onChange(code.product);
        onRefresh(false, false, false);
    }

    const onRefresh = (lines: boolean, sizes: boolean, codes: boolean) => {
        if (lines) setLineListHash(Utils.getRandomKey());
        if (sizes) setSizeListHash(Utils.getRandomKey());
        if (codes) setCodeListHash(Utils.getRandomKey());
    }

    return {
        pickers,
        selectedLine,
        selectedSize,
        selectedCode,

        lineListHash,
        sizeListHash,
        codeListHash,

        getPicker,
        shouldShow,
        onLineClick,
        onSizeClick,
        onCodeClick
    }
}